import { api } from "@/api/client";

// Pezzi adattivi: su rete mobile i pezzi da 5 MB cadevano in timeout e il
// caricamento si piantava a una percentuale. Ora si parte prudenti e, se un
// pezzo fallisce, si scende di taglia invece di insistere.
const CHUNK_SMALL = 512 * 1024;         // 512 KB (rete scarsa o dopo errori)
const CHUNK_MID = 1024 * 1024;          // 1 MB   (default su mobile)
const CHUNK_BIG = 4 * 1024 * 1024;      // 4 MB   (wifi / rete veloce)
const MAX_RETRIES = 6;
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024; // 500 MB
export const MAX_UPLOAD_LABEL = "500 MB";

const isSlowNetwork = () => {
  const t = navigator.connection?.effectiveType;
  return t === "slow-2g" || t === "2g" || t === "3g";
};

const chunkSizeFor = (file) => {
  if (isSlowNetwork()) return CHUNK_SMALL;
  if (navigator.connection?.effectiveType === "4g" && !navigator.connection?.saveData) {
    return file.size > 10 * 1024 * 1024 ? CHUNK_BIG : CHUNK_MID;
  }
  return CHUNK_MID;
};

// Su rete debole meno richieste insieme: in parallelo si intasano e ripartono.
const concurrencyFor = () => (isSlowNetwork() ? 1 : 3);

const sessionKey = (file, chunkSize) => `upload:${file.name}:${file.size}:${file.lastModified || 0}:${chunkSize}`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function startSession(file, chunkSize) {
  const key = sessionKey(file, chunkSize);
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      const { data } = await api.get(`/files/upload/${saved}/status`);
      // Un pezzo conta come fatto solo se tutti i suoi byte sono arrivati.
      const got = new Map();
      for (const [pos, len] of data.ranges || []) got.set(pos, len);
      const done = new Set();
      const total = Math.ceil(file.size / chunkSize);
      for (let i = 0; i < total; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        let cursor = start;
        while (cursor < end && got.has(cursor)) cursor += got.get(cursor);
        if (cursor >= end) done.add(i);
      }
      return { uploadId: saved, done };
    } catch {
      localStorage.removeItem(key);
    }
  }
  const { data: init } = await api.post("/files/upload/init", {
    filename: file.name,
    content_type: file.type || null,
    size: file.size,
  });
  localStorage.setItem(key, init.upload_id);
  return { uploadId: init.upload_id, done: new Set() };
}

/** Carica un file a pezzi, in parallelo, con ripresa automatica e ritenta sui pezzi caduti.
 * onProgress(0-100). Ritorna il documento file creato. */
export async function uploadFileChunked(file, onProgress) {
  const chunkSize = chunkSizeFor(file);
  const { uploadId, done } = await startSession(file, chunkSize);
  const total = Math.ceil(file.size / chunkSize);
  const startedAt = Date.now();
  const doneAtStart = done.size;
  const report = () => {
    const pct = Math.min(99, Math.round((done.size / total) * 100));
    const sentChunks = done.size - doneAtStart;
    const elapsed = (Date.now() - startedAt) / 1000;
    let kbps = null;
    let etaSeconds = null;
    if (sentChunks > 0 && elapsed > 1) {
      const bytesPerSec = (sentChunks * chunkSize) / elapsed;
      kbps = Math.round(bytesPerSec / 1024);
      etaSeconds = Math.round(((total - done.size) * chunkSize) / bytesPerSec);
    }
    onProgress?.(pct, { kbps, etaSeconds, sentBytes: done.size * chunkSize, totalBytes: file.size });
  };
  report();

  const pending = [];
  for (let i = 0; i < total; i++) if (!done.has(i)) pending.push(i);

  const sendOne = async (i) => {
    let attempt = 0;
    for (;;) {
      // Un pezzo che non passa viene spezzato in due e inviato come due
      // sotto-pezzi: così una rete lenta non blocca per sempre il caricamento.
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const parts = attempt >= 2 ? 4 : attempt >= 1 ? 2 : 1;
      try {
        const step = Math.ceil((end - start) / parts);
        for (let p = 0; p < parts; p++) {
          const s = start + p * step;
          if (s >= end) break;
          const blob = file.slice(s, Math.min(s + step, end));
          const fd = new FormData();
          fd.append("upload_id", uploadId);
          fd.append("index", String(i));
          fd.append("offset", String(s));
          fd.append("chunk", blob, file.name);
          await api.post("/files/upload/chunk", fd, { timeout: 90000 });
        }
        done.add(i);
        report();
        return;
      } catch (e) {
        attempt += 1;
        if (attempt > MAX_RETRIES) throw e;
        await sleep(Math.min(15000, 1000 * 2 ** (attempt - 1)));
      }
    }
  };

  // Pool di invii paralleli, ridotto automaticamente sulle reti lente.
  let cursor = 0;
  const workers = Array.from({ length: Math.min(concurrencyFor(), pending.length) }, async () => {
    for (;;) {
      const idx = cursor++;
      if (idx >= pending.length) return;
      await sendOne(pending[idx]);
    }
  });
  await Promise.all(workers);

  const fd = new FormData();
  fd.append("upload_id", uploadId);
  const { data } = await api.post("/files/upload/complete", fd, { timeout: 300000 });
  localStorage.removeItem(sessionKey(file, chunkSize));
  onProgress?.(100);
  return data;
}

/** Riduce una foto (max 1600px, JPEG q0.8) prima dell'invio; se non riesce, usa l'originale. */
export async function compressImage(file, maxSide = 1600) {
  if (!file || !/^image\//.test(file.type || "") || file.size < 400 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.8));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}

/** Foto: compressione + invio a pezzi con ripresa automatica. Ritorna il documento file. */
export async function uploadPhoto(file, onProgress) {
  const f = await compressImage(file);
  return uploadFileChunked(f, onProgress);
}
